import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, matchPath } from "react-router-dom";import { HelmetProvider, Helmet } from "react-helmet-async";
import SEO from "@/components/site/SEO";
import HeadDedupe from "@/components/site/HeadDedupe";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ScrollToTop from "@/components/site/ScrollToTop";
import Header from "@/components/site/Header";
import BottomTabBar from "@/components/site/BottomTabBar";
import Footer from "@/components/site/Footer";
import Schools from "@/pages/Schools";
import SchoolDetail from "@/pages/SchoolDetail";
import Forum from "@/pages/Forum";
import ThreadDetail from "@/pages/ThreadDetail";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
// AuthCallback is deprecated (Google popup+callback flow, Feb 2026) — kept
// as a file for git history but no longer routed.
// import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import SubmitSchool from "@/pages/SubmitSchool";
import Compare from "@/pages/Compare";
import Admin from "@/pages/Admin";
import ReviewSuccess from "@/pages/ReviewSuccess";
import StaticPage from "@/pages/StaticPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RecruitModerator from "@/pages/RecruitModerator";
import SchoolAnalytics from "@/pages/SchoolAnalytics";
import AlumniAnalytics from "@/pages/AlumniAnalytics";
import Brands from "@/pages/Brands";
import BrandDetail from "@/pages/BrandDetail";
import SchoolGroupDetail from "@/pages/SchoolGroupDetail";
import CampusDetail from "@/pages/CampusDetail";
import SubForum from "@/pages/SubForum";
import MyReviews from "@/pages/MyReviews";
import Onboarding from "@/pages/Onboarding";
import RuangHome from "@/pages/RuangHome";
import RuangDirectory from "@/pages/RuangDirectory";
import RuangDetail from "@/pages/RuangDetail";
import DiscussionDetail from "@/pages/DiscussionDetail";
import MyBookmarks from "@/pages/MyBookmarks";
import FeatureDisabled from "@/pages/FeatureDisabled";
import { useFlags } from "@/lib/flags";

// Feature gates (Editorial pivot): render the page only when its server flag is on.
function ForumGate({ children }) {
  const { loaded, standalone_forum_enabled } = useFlags();
  if (!loaded) return null;
  return standalone_forum_enabled ? children : <FeatureDisabled feature="forum" />;
}

function ReviewsGate({ children }) {
  const { loaded, school_reviews_enabled } = useFlags();
  if (!loaded) return null;
  return school_reviews_enabled ? children : <FeatureDisabled feature="reviews" />;
}

function AppRouter() {
  // Google OAuth via popup + JS callback (Feb 2026) — no more `#session_id`
  // hash roundtrip, so the previous AuthCallback route is no longer needed.
  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0">
        <Routes>
          {/* Editorial pivot: The Reports listing is the homepage. */}
          <Route path="/" element={<Blog />} />
          {/* Localized (Indonesian) routes — canonical from Phase 3 onward */}
          <Route path="/sekolah" element={<Schools />} />
          <Route path="/sekolah/usulkan" element={<SubmitSchool />} />
          <Route path="/sekolah/:id" element={<SchoolDetail />} />
          <Route path="/grup/:slug" element={<SchoolGroupDetail />} />
          <Route path="/cabang/:slug" element={<CampusDetail />} />
          {/* Legacy English routes — redirect for backward-compat & SEO */}
          <Route path="/schools" element={<LegacySchoolsRedirect />} />
          <Route path="/schools/submit" element={<Navigate to="/sekolah/usulkan" replace />} />
          <Route path="/schools/:id" element={<LegacySchoolDetailRedirect />} />
          <Route path="/school-groups/:slug" element={<LegacyGroupRedirect />} />
          <Route path="/campuses/:slug" element={<LegacyCampusRedirect />} />
          <Route path="/forum" element={<ForumGate><RuangHome /></ForumGate>} />
          <Route path="/forum/beranda" element={<ForumGate><Navigate to="/forum" replace /></ForumGate>} />
          <Route path="/forum/ruang" element={<ForumGate><RuangDirectory /></ForumGate>} />
          <Route path="/forum/ruang/:slug" element={<ForumGate><RuangDetail /></ForumGate>} />
          <Route path="/forum/diskusi/:id" element={<ForumGate><DiscussionDetail /></ForumGate>} />
          <Route path="/forum/legacy/:id" element={<ForumGate><ThreadDetail /></ForumGate>} />
          <Route path="/forum/sub/:subSlug" element={<ForumGate><SubForum /></ForumGate>} />
          <Route path="/forum/*" element={<ForumGate>{null}</ForumGate>} />
          <Route path="/onboarding" element={<ForumGate><Onboarding /></ForumGate>} />
          <Route path="/jurnal" element={<JurnalListingRedirect />} />
          <Route path="/jurnal/:slug" element={<BlogDetail />} />
          <Route path="/pratinjau/:previewId" element={<ArticlePreview />} />
          {/* Legacy /blog routes — redirect to the editorial homepage / article */}
          <Route path="/blog" element={<JurnalListingRedirect />} />
          <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/me/reviews" element={<ReviewsGate><MyReviews /></ReviewsGate>} />
          <Route path="/me/bookmarks" element={<MyBookmarks />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/review-success" element={<ReviewsGate><ReviewSuccess /></ReviewsGate>} />
          <Route path="/halaman/:slug" element={<StaticPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/relawan-moderator" element={<RecruitModerator />} />
          <Route path="/brands" element={<Navigate to="/sekolah?mode=grup" replace />} />
          <Route path="/brands/:brandId" element={<BrandDetail />} />
          <Route path="/admin/analytics/:id" element={<SchoolAnalytics />} />
          <Route path="/admin/alumni-analytics" element={<AlumniAnalytics />} />
        </Routes>
      </main>
      <Footer />
      <BottomTabBar />
    </>
  );
}

/**
 * SeoAwareRedirect — replaces bare `<Navigate>` for legacy URL paths so
 * search engines get real 301-equivalent signals:
 *
 *  1. `<link rel="canonical" href="...">` pointing at the target URL so any
 *     bot that snapshots this HTML learns the definitive location.
 *  2. `<meta http-equiv="refresh" content="0; url=...">` — the closest we can
 *     get to an HTTP 301 given the platform ingress routes non-`/api` paths
 *     straight to the React SPA (FastAPI never sees `/schools/*` requests).
 *  3. A `<noscript>` link so pre-JS crawlers can still follow the redirect.
 *
 * Users continue to see the near-instant SPA redirect via `<Navigate replace>`.
 */
function SeoAwareRedirect({ to }) {
  const absoluteTarget = typeof window !== "undefined" ? `${window.location.origin}${to}` : to;
  return (
    <>
      <Helmet>
        <link rel="canonical" href={absoluteTarget} />
        <meta name="robots" content="noindex, follow" />
        <meta httpEquiv="refresh" content={`0; url=${to}`} />
      </Helmet>
      <noscript>
        <p>
          Halaman ini telah pindah. Buka{" "}
          <a href={to}>{absoluteTarget}</a>.
        </p>
      </noscript>
      <Navigate to={to} replace />
    </>
  );
}

function ArticlePreview() {
  const { previewId } = useParams();
  return <BlogDetail previewId={previewId} />;
}

function JurnalListingRedirect() {
  // /jurnal and /blog → / (editorial homepage). Query string (tag filters) preserved.
  const { search } = useLocation();
  return <SeoAwareRedirect to={`/${search}`} />;
}

function LegacyBlogRedirect() {
  // Preserves the slug when redirecting /blog/:slug → /jurnal/:slug
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/blog\//, "");
  return <SeoAwareRedirect to={`/jurnal/${slug}`} />;
}

function LegacySchoolsRedirect() {
  // /schools → /sekolah  (preserve ?mode= etc so bookmarks with query strings still work)
  const { search } = useLocation();
  return <SeoAwareRedirect to={`/sekolah${search}`} />;
}

function LegacySchoolDetailRedirect() {
  // /schools/<slug-or-id> → /sekolah/<slug-or-id>
  const { pathname, search } = useLocation();
  const tail = pathname.replace(/^\/schools\//, "");
  return <SeoAwareRedirect to={`/sekolah/${tail}${search}`} />;
}

function LegacyGroupRedirect() {
  // /school-groups/:slug → /grup/:slug
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/school-groups\//, "");
  return <SeoAwareRedirect to={`/grup/${slug}`} />;
}

function LegacyCampusRedirect() {
  // /campuses/:slug → /cabang/:slug
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/campuses\//, "");
  return <SeoAwareRedirect to={`/cabang/${slug}`} />;
}

/**
 * SeoFallback — App-level default SEO. Deterministic ownership rule
 * (Sep 2026): routes whose page component renders its own <SEO/> are listed
 * in SEO_OWNED_ROUTE_PATTERNS, and the fallback emits NOTHING there — no
 * fallback title, description, canonical, or og:url competing with the
 * route-level owner. Routes not listed (home, /jurnal listing, directory,
 * auth pages, legal, etc.) keep the generic fallback.
 */
const SEO_OWNED_ROUTE_PATTERNS = [
  "/",                    // Blog (The Reports listing = homepage)
  "/jurnal",              // JurnalListingRedirect
  "/pratinjau/:previewId", // ArticlePreview (noindex)
  "/blog",                // JurnalListingRedirect
  "/jurnal/:slug",        // BlogDetail
  "/grup/:slug",          // SchoolGroupDetail
  "/cabang/:slug",        // CampusDetail
  "/sekolah/usulkan",     // SubmitSchool (owns SEO incl. noindex,follow)
  "/forum",               // RuangHome
  "/forum/*",             // RuangDirectory / RuangDetail / DiscussionDetail / ThreadDetail
  "/onboarding",          // Onboarding
  "/me/bookmarks",        // MyBookmarks
];

const REVIEW_GATED_ROUTES = ["/me/reviews", "/review-success"];

function SeoFallback() {
  const { pathname } = useLocation();
  const { loaded, school_reviews_enabled } = useFlags();
  const owned =
    SEO_OWNED_ROUTE_PATTERNS.some((p) => matchPath({ path: p, end: true }, pathname)) ||
    (loaded && !school_reviews_enabled && REVIEW_GATED_ROUTES.includes(pathname)) ||
    // /sekolah/:id → SchoolDetail owns SEO. (/sekolah/usulkan is listed
    // explicitly above so the wildcard exclusion is no longer needed.)
    !!matchPath({ path: "/sekolah/:id", end: true }, pathname);
  if (owned) return null;
  return <SEO />;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            {/* Fallback SEO — suppressed on routes whose page owns its SEO.
                Must live INSIDE BrowserRouter: SeoFallback calls useLocation. */}
            <SeoFallback />
            {/* HeadDedupe MUST live inside BrowserRouter so it can subscribe to route changes. */}
            <HeadDedupe />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
